import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Layers, Wallet, ListChecks, FileDown, Play, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEPS = [
  {
    icon: Wallet,
    title: "1. Unlock a SpaceCost project",
    body: "Pay R180 once-off via Paystack. You get one project that can hold up to 3 rooms — enough for a kitchen + scullery + adjacent bathroom or any tight zone you're building.",
  },
  {
    icon: Layers,
    title: "2. Add your rooms",
    body: "Pick the room type — kitchen, bathroom, garden, office, nursery — and SpaceCost seeds default categories: demolition, joinery, plumbing, electrical, appliances, decor, labour, contingency. Edit anything, add line items with qty / unit / unit cost.",
  },
  {
    icon: ListChecks,
    title: "3. Track Estimate vs Actual",
    body: "Toggle Actual mode as receipts come in. Each room shows budgeted vs actual spend with variance front-and-centre, so a R200 over-spend on tile doesn't sneak past you.",
  },
  {
    icon: FileDown,
    title: "4. Export, share or take to your contractor",
    body: "Generate a print-ready PDF, or share the live project with a partner, designer or contractor — view-only or full edit. Email delivery via Resend, signed access only.",
  },
];

export default function SpaceCostHowItWorks() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAF9] text-[#1C1C1C] fc-fade-in" data-theme="spacecost" data-testid="spacecost-how-it-works">
      <header className="sticky top-0 z-30 fc-header-bar">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <button
            data-testid="howitworks-back"
            onClick={() => navigate("/spacecost")}
            className="inline-flex items-center gap-2 text-[13px] text-[#57534E] hover:text-[#1C1C1C]"
          >
            <ArrowLeft className="w-4 h-4" /> SpaceCost
          </button>
          <div className="flex items-baseline gap-2.5">
            <span className="fc-heading text-xl sm:text-2xl font-extrabold tracking-tight text-[#1C1C1C] leading-none">SpaceCost</span>
            <span className="hidden sm:inline text-[9px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
              by MyPlanCost
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-20">
        <div className="fc-label text-sage-700">How it works</div>
        <h1 className="fc-heading text-4xl sm:text-5xl font-medium tracking-tight mt-3 leading-[1.05]">
          From idea to budget in four steps.
        </h1>
        <p className="text-base text-[#57534E] mt-5 leading-relaxed">
          SpaceCost is room-level. No property valuations, no flip math, no whole-house exterior or systems —
          just the numbers for the actual space you're building.
        </p>

        {/* Mobile-only inline CTA row — matches the FlipCost How-it-works
            pattern. Clarity recordings show mobile visitors scroll past
            header buttons immediately, so we surface the same actions right
            after the lead-in paragraph where the eye actually lands. */}
        <div
          className="md:hidden mt-6 flex flex-wrap items-center gap-2"
          data-testid="sc-how-mobile-cta-row"
        >
          <button
            type="button"
            data-testid="sc-how-mobile-faq-btn"
            onClick={() => navigate("/faq")}
            className="h-10 px-3.5 rounded-lg border border-black/[0.08] hover:border-black/[0.25] bg-white hover:bg-[#F5F5F4] text-sm text-[#27272A] inline-flex items-center gap-1.5 transition-colors"
          >
            <HelpCircle className="w-4 h-4 text-[#78716C]" /> FAQ
          </button>
          <button
            type="button"
            data-testid="sc-how-mobile-pricing-btn"
            onClick={() => navigate("/spacecost/pricing")}
            className="h-10 px-3.5 rounded-lg bg-sage-100 hover:bg-sage-200 border border-sage-200 text-sage-700 text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            <Play className="w-3.5 h-3.5" /> See pricing
          </button>
          <button
            type="button"
            data-testid="sc-how-mobile-get-started-btn"
            onClick={() => navigate("/auth?mode=register&product=spacecost")}
            className="h-10 px-3.5 rounded-lg bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            Get started <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <ol className="mt-12 space-y-6" data-testid="steps">
          {STEPS.map((s) => (
            <li key={s.title} className="fc-card p-6 flex gap-4 items-start">
              <div className="w-11 h-11 rounded-lg bg-sage-100 border border-sage-200 grid place-items-center text-sage-700 shrink-0">
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="fc-heading text-[15px] text-[#1C1C1C]">{s.title}</div>
                <p className="text-[13px] text-[#57534E] mt-2 leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Button
            data-testid="howitworks-cta-register"
            onClick={() => navigate("/auth?mode=register&product=spacecost")}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-11 px-5"
          >
            Start your first project <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button
            data-testid="howitworks-cta-pricing"
            onClick={() => navigate("/spacecost/pricing")}
            variant="ghost"
            className="text-[#57534E] hover:text-[#1C1C1C]"
          >
            See pricing
          </Button>
        </div>
      </main>
    </div>
  );
}
